import { Form as VeeForm, Field as VeeField, defineRule, ErrorMessage, FieldArray, configure } from 'vee-validate'
import SubmissionComputed from 'src/pages/submissions/script/common.computed'
import { isArray, isEmpty } from 'lodash'
import Misc, { FOUNDRY_NAME_INVALID_PATTERN, SSN_INVALID_PATTERN, validateAlphanumeric } from 'src/utils/helper/misc'
import { MAX_SIZE_INGESTION_FAMILYNAME, MIN_SIZE_INGESTION_FAMILYNAME, MIN_ORDER_MID_LENGTH, REGEX_COLLECTIONS, MAX_SIZE_INGESTION_FAMILYNAME_CUSTOM } from 'src/utils/constants/common'
import Content from 'src/assets/i18n/en.json'
import get from 'lodash/get'
import { required, min, numeric, between, max, alpha_num, email, min_value, max_value, is } from '@vee-validate/rules'
import i18n from 'src/assets/i18n/i18n'
import { adobeCjaEventEmitter } from 'modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_CATEGORIES, LINK_SECTION, LINK_NAMES, LINK_TYPES } from 'modules/adobe-cja/constants'

const { t } = i18n().global
const {
  EMOJI_REGEX,
  HTML_SYMBOLS_REGEX,
  SCRIPT_TAG_REGEX,
  SAFE_HTML_TAG_NAME_REGEX,
  SAFE_TAG_ATTR_REGEX,
  SAFE_ENCODED_HTML_TAG_REGEX,
  SAFE_ENCODED_TAG_ATTR_REGEX,
  ENCODED_ANGLE_BRACKETS_REGEX,
  ALLOWED_CHARS_PATTERN_FOR_FAMILY_NAME
} = REGEX_COLLECTIONS;

function configureVeeValidateComponents(app) {
  app.component('VeeForm', VeeForm)
  app.component('VeeField', VeeField)
  app.component('ErrorMessage', ErrorMessage)
  app.component('FieldArray', FieldArray)
}

function configureVeeValidateRules() {
  defineRule('required', required)
  defineRule('min_value', min_value)
  defineRule('max_value', max_value)
  defineRule('email', email)
  defineRule('fou_numbers', numeric)
  defineRule('min', min)
  defineRule('max', max)
  defineRule('fou_slides', between)
  defineRule('between', between)
  defineRule('alpha_num', alpha_num)
  defineRule('numeric', numeric)
  defineRule('is', is)
  defineRule(
    'fou_binaryservice',
    () =>
      `Your fonts are still being processed. Please wait a few hours and check back in.
      If they're still not processed, please delete this submission, create a new submission,
      and upload the fonts again. If after 12 hours you're still encountering this message,
        please contact us at <a class="mt-link" href="mailto:foundrysupport@monotype.com">foundrysupport@monotype.com.</a>`
  )
  defineRule('script_tag_validate', (value, _, context) => {
    if (Misc.validateScriptRegex(value)) return true
    const fieldName = context.field
    let partNumber = 'Please do not use script tag in part number.'
    let genericMessage = `Please do not use script tag in ${fieldName}.`
    return !isEmpty(context.rule.params[0]) ? partNumber : genericMessage
  })

  defineRule('html_tag_validate', (value, _, context) => {
    if (Misc.validateHtmlTags(value)) return true
    const fieldName = context.field
    let partNumber = 'Please do not use html tag in part number.'
    let genericMessage = `Please do not use html tag in ${fieldName}.`
    return !isEmpty(context.rule.params[0]) ? partNumber : genericMessage
  })

  defineRule('no_emoji', (value, _, context) => {
    const emojiRegex = /[\u{1F300}-\u{1F5FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}]/u;
    if (!emojiRegex.test(value)) {
      return true;
    }
  });

  defineRule('alpha_num_space_ampersand', (value, _, context) => {
    if (context.rule.params[0] === 'true') return true
    if (isEmpty(value) || Misc.validateCustomRegex(value, /^[\p{L}\p{M}\p{N}&. ]+$/u)) return true
    const fieldName = context.field
    return `Your ${fieldName} can only contain alphanumeric characters and the ampersand.`
  })

  defineRule('caps_all', (value, _, context) => {
    if (context.rule.params[0] === 'true') return true
    if (isEmpty(value) || Misc.validateAllCaps(value)) return true
    const fieldName = context.field
    return `Your ${fieldName} cannot be in all caps.`
  })

  defineRule('alpha_num_space', (value, _, context) => {
    if (context.rule.params[0] === 'true') return true
    if (isEmpty(value) || Misc.validateCustomRegex(value)) return true
    const fieldName = context.field
    return `The ${fieldName} field should have alpha numeric characters.`
  })

  defineRule('fou_numbers', (value, _, context) => {
    const fieldName = context.field
    if (!isEmpty(value) && !value.match(/^[0-9.]+$/)) {
      return `The ${fieldName} field should be a valid price.`
    }
    return true
  })

  defineRule('min_limit', (value, _, context) => {
    const fieldName = context.value
    if (fieldName <= 5 && context.rule.params[1] == 'false') {
      return 'Font family pricing must be above $5. Please adjust.'
    }
    return true
  })

  defineRule('max_decimal', (value, _, context) => {
    const fieldName = context.field
    if (parseFloat(value) > parseFloat(context.rule.params[0])) {
      return `The ${fieldName} must be ${context.rule.params[0]} or less.`
    }
    return true
  })

  defineRule('price_rule', (value, _, context) => {
    const fieldName = context.field
    if (parseFloat(value) > parseFloat(context.rule.params[0])) {
      return `The ${fieldName} is greater than the complete family pack price.`
    }
    if (
      parseFloat(value) <= 0 &&
      context.field.includes('$') &&
      context.rule.params[1] === 'false') {
      return `The ${fieldName} must be above $0. Please adjust.`
    }
    return true
  })

  defineRule('av_check_rule', (value, _, context) => {
    if (context.rule.params[0] && context.rule.params[0].length > 0) {
      return `Our security system has detected a malware in your image. Please re-check and upload again: ${context.rule.params[0]}`
    }
    return true
  })

  defineRule('custom_required', (value, _, context) => {
    const fieldName = context.field
    if (Misc.sanitizeHTML(value)) return true
    return `The ${fieldName} field is required.`
  })

  defineRule('custom_dropdowncheck', (value, _, context) => {
    const fieldName = context.field
    const ruleName = context.rule.params[0]
    const prevElem = document.getElementById(ruleName).previousElementSibling
    const child = prevElem.children[0].innerHTML
    if (child === '') {
      return `The ${fieldName} is required`
    }

    return true
  })

  defineRule('caps_alpha_first', (value, _, context) => {
    if (context.rule.params[0] === 'true') return true
    if (isEmpty(value) || Misc.validateFirstAlphaCaps(value)) return true
    const fieldName = context.field
    return `The first letter of your ${fieldName} should be capitalized.`
  })
  defineRule('validate_website_url', (value, _, context) => {
    if (!value) return true
    const url = /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.){1,}[a-zA-Z]{2,}(\S*)$/
    const isValidUrl = url.test(value)
    if (!isValidUrl && !isEmpty(value)) {
      return 'Invalid foundry website URL.'
    }
    return true
  })

  defineRule('validate_email', (value, _, context) => {
    const emailRegex = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    const isValidEmail = emailRegex.test(value)
    if (!isValidEmail) return "Invalid primary contact's email address."

    return true
  })

  defineRule('validate_phone_number', (value, _, context) => {
    if (!value) return true
    const phoneNumberRegex = /^\+?\d{1,3}[-.\s]?\(?\d{1,3}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/
    const isValidPhoneNumber = phoneNumberRegex.test(value)
    if (isValidPhoneNumber && value === '') {
      return true
    }
    return 'Invalid Telephone number'
  })

  defineRule('validate_zip_code', (value, _, context) => {
    const zipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/
    const isValidZipCode = zipCodeRegex.test(value)
    if (isValidZipCode) {
      return true
    }
    return 'Invalid Zip code.'
  })
  defineRule('font_types_string', (value, _, context) => {
    const fieldName = context.field
    if (
      !Misc.validateCustomRegex(
        value,
        /^(?!.*(otf|ttf|OTF|TTF|OpenType|opentype|Opentype|Truetype|TrueType|truetype)).*$/
      )
    ) {
      return `Please do not include the file type in your ${fieldName}`
    }
    return true
  })
  defineRule('custom_max_char', (value, _, context) => {
    if (value.length > Number(context.rule.params)) {
      return 'Character limit exceeded.'
    }
    return true
  })
  defineRule('custom_max_char_no_html', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, '')
    const stringWithoutSpaces = strippedString.trim()
    if (stringWithoutSpaces.length > Number(context.rule.params)) {
      return 'Character limit exceeded.'
    }
    return true
  })

  defineRule('custom_max_char_no_description', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, "");
    if (strippedString.length > Number(context.rule.params)) {
      return 'The descriptions field may not be greater than 64k characters.'
    }
    return true
  })

  defineRule('custom_min_max_char_no_description', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, "");
    const canBeEmpty = 0 === Number(context.rule.params[0]),
      min = Number(context.rule.params[1]),
      max = Number(context.rule.params[2]),
      stringLength = strippedString.length;
    if (canBeEmpty && stringLength === 0) {
      return true
    } else if (stringLength < min || stringLength > max) {
      return t('pages.inventory_ingestion_hub.dropzone.errorStrings.description_min_length', {
        min,
        max
      });
    }
    return true
  })

  defineRule('custom_min_char_no_html', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, '')
    const stringWithoutSpaces = strippedString.trim()
    if (stringWithoutSpaces.length == 0) {
      return 'The foundry description field is required.'
    }
    if (stringWithoutSpaces.length < Number(context.rule.params)) {
      return 'The long foundry description must be longer than 350 characters.'
    }

    return true
  })

  defineRule('custom_min_char_no_html_bundles', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, '')
    const stringWithoutSpaces = strippedString.trim()
    if (stringWithoutSpaces.length == 0) {
      return false
    }
    return true
  })


  defineRule('fou_date_between', (value, _, context) => {
    const fieldName = context.field
    let startDate = new Date(context.rule.params[0]).setHours(0, 0, 0, 0)
    let endDate = new Date(context.rule.params[1]).setHours(23, 59, 59, 0)
    let compareDate = new Date(value).getTime()

    let isValid = compareDate > startDate && compareDate < endDate // compare dates
    if (!isValid) {
      return `The ${fieldName} should be between ${Misc.formatDate(context.rule.params[0], 'll')} and ${Misc.formatDate(
        context.rule.params[1],
        'll'
      )}.`
    }
    return true
  })

  defineRule('uniqueStyle', (value, _, context) => {
    const trimAndLowerCaseValue = Misc.dashedString(value).toLowerCase().trim()
    const fieldName = context.field
    let breakLength = context.rule.params.shift()
    let packageArray = context.rule.params.splice(0, breakLength)
    let styleArray = context.rule.params
    const trimAndLowerCasePackageArray = packageArray.map(name => {
      return Misc.dashedString(name).toLowerCase().trim()
    })
    const duplicatesPack = Misc.getDuplicates(trimAndLowerCasePackageArray)
    const trimAndLowerCaseStyleArray = styleArray.map(name => {
      return Misc.dashedString(name).toLowerCase().trim()
    })
    const isPackageArrayEmpty = isEmpty(packageArray) || (packageArray.length === 1 && packageArray[0] === '')
    const isStyleArrayEmpty = isEmpty(styleArray) || (styleArray.length === 1 && styleArray[0] === '')
    const duplicatesStyle = Misc.getDuplicates(trimAndLowerCaseStyleArray)
    if (
      (duplicatesPack.indexOf(trimAndLowerCaseValue) >= 0 || isPackageArrayEmpty) &&
      (duplicatesStyle.indexOf(trimAndLowerCaseValue) >= 0 || isStyleArrayEmpty)
    ) {
      return `The ${fieldName} should be unique.`
    }
    return true
  })
  defineRule('array_script_tag_validate', (value, _, context) => {
    if (!Misc.validateScriptRegex(value)) {
      // Todo: implement better way to handle this instead of using split
      const splitField = context.field.split('.')
      const fieldName = splitField[1].split('_').join(' ')
      let partNumber = 'Please do not use script tag in part number'
      let genericMessage = `Please do not use script tag in  ${fieldName}`
      return context.field.includes('externalsku') ? partNumber : genericMessage
    }
    return true
  })
  defineRule('state_validate', (value, _, context) => {
    const fieldName = context.field
    const states = ['AU', 'CA', 'US']
    if (states.includes(context.form?.country?.value) && isEmpty(value)) {
      return `The ${fieldName} is required. `
    }
    return true
  })

  // to check whether the foundry selected all the checkbox during registration journey
  defineRule('check_foundry_agreements', value => {
    if (value.length < 4) {
      return 'You must check all boxes to proceed. Please read all information carefully.'
    }
    return true
  })

  defineRule('special_chars_not_allowed', value => {
    if (Misc.validateSpecialChars(value, FOUNDRY_NAME_INVALID_PATTERN)) {
      return 'The foundry name field should not have alpha numeric characters'
    }
    return true
  })

  defineRule('custom_min_char_rte', (value, _, context) => {
    const fieldName = context.field
    if (Misc.customStringRTE(value).length < Number(context.rule.params)) {
      return `The ${fieldName} must be at least ${context.rule.params[0]} characters.`
    }
    return true
  })

  defineRule('not_empty', (value, _, context) => {
    const fieldName = context.field.split('_').join(' ')
    if (isEmpty(value)) return `The ${fieldName} field cannot be empty`
    return true
  })

  defineRule('array_custom_min_char_rte', (value, _, context) => {
    // Todo: implement better way to handle this instead of using split
    const splitField = context.field.split('.')
    const fieldName = splitField[1].split('_').join(' ')
    if (Misc.customStringRTE(value).length < Number(context.rule.params)) {
      return `The ${fieldName} must be at least ${context.rule.params[0]} characters.`
    }
    return true
  })

  defineRule('array_required', (value, _, context) => {
    // Todo: implement better way to handle this instead of using split
    const splitField = context.field.split('.')
    const fieldName = splitField[1].split('_').join(' ')
    if (isEmpty(value)) {
      return `The ${fieldName} field is required.`
    }
    return true
  })

  // Sales Report Date Filter should not be before 1 Jan 2010
  defineRule('validate_max_date', (value, _, context) => {
    /* As per documentation: https://vee-validate.logaretm.com/v4/guide/global-validators#defining-global-validators, we can also return a promise to validate fields. Also, this pattern is followed so that, there is less overhead to migrate vee validate rules. */
    if (
      context.rule.params[0].length > 0 &&
      value.length > 0 &&
      Misc.checkDateisAfterAnotherDate(value, '2010-01-01')
    ) {
      return new Promise(resolve => {
        resolve(Misc.checkDateisAfterAnotherDate(context.rule.params[0], value))
      })
    }
    return new Promise(resolve => {
      resolve(true)
    })
  })

  // Sales Report Date Filter should not be before 1 Jan 2010
  defineRule('validate_min_date', (value, _, context) => {
    if (value.length > 0) {
      return new Promise(resolve => {
        resolve(Misc.checkDateisAfterAnotherDate(value, '2010-01-01'))
      })
    }
    return new Promise(resolve => {
      resolve(true)
    })
  })

  // Sales Report End Date should not be less than start date
  defineRule('validate_end_date', (value, _, context) => {
    if (context.rule.params[0].length > 0 && value.length > 0) {
      return new Promise(resolve => {
        resolve(Misc.checkDateisAfterAnotherDate(value, context.rule.params[0]))
      })
    }
    return new Promise(resolve => {
      resolve(true)
    })
  })

  defineRule('numeric_only_no_space', (value, _, context) => {
    return Misc.validateCustomRegex(value, /^[0-9]*$/)
  })

  defineRule('numeric_only', (value, _, context) => {
    return Misc.validateCustomRegex(value, /^[0-9 ]*$/)
  })

  defineRule('decimal', (value, _, context) => {
    const decimals = context.rule.params[0]
    const separator = context.rule.params[1] || '.'

    if (Number(context.rule.params[0]) === 0) {
      return /^-?\d*$/.test(value)
    }

    const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`
    const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`)

    return regex.test(value)
  })

  defineRule('custom_regex', value => {
    return Misc.validateSpecialChars(value, SSN_INVALID_PATTERN)
  })

  defineRule('binary_amount', value => {
    if (value) return true
    return 'We cannot generate the preview as no fonts have been uploaded.'
  })

  defineRule('custom_numeric', value => {
    return Misc.validateCustomRegex(value, /[-1234567890]/)
  })

  defineRule('alpha_numeric_no_space', value => {
    return Misc.validateCustomRegex(value, /^[a-zA-Z0-9]+$/)
  })

  defineRule('alpha_numeric', value => {
    return Misc.validateCustomRegex(value, /^[a-zA-Z0-9 ]+$/)
  })

  defineRule('promo_back_date', (value, _, context) => {
    if (context.rule.params[1] === 'true') {
      return true
    }
    let compareDate = new Date(context.rule.params[0])
    return new Date(value) >= compareDate // compare dates
  })

  defineRule('max_date_2_years', (value) => {
    const startDate = new Date(Date.UTC(new Date(value).getUTCFullYear(), new Date(value).getUTCMonth(), new Date(value).getUTCDate())).getTime();
    const today = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()));
    const maxAllowedDate = new Date(today);
    maxAllowedDate.setDate(today.getDate() + 1); // Add 1 day to today
    maxAllowedDate.setFullYear(maxAllowedDate.getFullYear() + 2); // Add 2 years

    if (startDate <= maxAllowedDate.getTime()) {
      return true;
    }
  });

  defineRule('checkUniqueSecretCode', (value, _, context) => {
    if (!value) return true
    return context.rule.params[0] === 'true'
  })

  defineRule('max_days', (value, _, context) => {
    const [startDate, secretCode, daysLimit] = context.rule.params
    if (secretCode) {
      return true
    } else {
      if (startDate.length > 0 && value.length > 0) {
        let days = Misc.getDays(value, startDate);
        return days <= daysLimit;
      }
      return true
    }
  })

  defineRule('bundle_image', (value, _, context) => {
    if (isEmpty(value)) return true
    if (value.length >= context.rule.params[0] && value.length <= context.rule.params[1]) {
      return true
    }
  })

  defineRule('check_1_day', (value, _, context) => {
    if (context.rule.params[0].length > 0 && value.length > 0) {
      let days =
        context.rule.params[1] === 'start'
          ? Misc.getDays(context.rule.params[0], value)
          : Misc.getDays(value, context.rule.params[0])
      return days >= 1
    }
    return true
  })

  defineRule('edit_max_days', (value, _, context) => {
    const [daysLimit, startDate = '', secretCode = '', promotionStatus = '', isFoundrySupport = ''] = context.rule.params
    let valid = true
    const isInactivePromotionOrFoundrySupport = promotionStatus !== 'active' || isFoundrySupport === 'true'
    const areDatesValid = Boolean(value) && Boolean(startDate)

    if (isInactivePromotionOrFoundrySupport && areDatesValid && !secretCode) {
      valid = Misc.getDays(value, startDate) <= daysLimit
    }

    return valid
  }
  );

  defineRule('max_discount_value', (value, _, context) => {
    const numericValue = parseFloat(value);
    const max = parseFloat(context.rule.params[0]);
    return numericValue <= max;
  })

  defineRule('font_types_string', (value, [isFoundrySupport]) => {
    let valid = true
    if (isFoundrySupport === 'false') {
      valid = Misc.validateCustomRegex(
        value,
        /^(?!.*(otf|ttf|OTF|TTF|OpenType|opentype|Opentype|Truetype|TrueType|truetype)).*$/
      )
    }
    return valid
  })

  defineRule('lengthChar', (value, [maxLength]) => {
    return Misc.validateSize(value, Number(maxLength))
  })

  defineRule('required_keys', (value, params) => {
    if (typeof value !== 'object' || value === null) {
      return false
    }
    return params.every(key => key in value)
  })

  defineRule('required_or_keys', (value, params) => {
    if (typeof value !== 'object' || value === null) {
      return false
    }

    return params.some(key => key in value)
  })

  defineRule('required_or_key_values', (value, params) => {
    if (typeof value !== 'object' || value === null) {
      return false
    }

    return params.some(key => key && key in value && value[key])
  })

  defineRule('required_key_values', (value, params) => {
    if (typeof value !== 'object' || value === null) {
      return false
    }

    if (Array.isArray(value)) {
      if (!value.length) return false

      return value.every(item => params.every(key => item && key && key in item && item[key]))
    }

    return params.every(key => key && key in value && value[key])
  })

  defineRule('telephone', (value, _, content) => {
    const isTelephone = Misc.validateCustomRegex(
      value,
      /^\s*(?:(\+|00)?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{3,4})(?: *x(\d+))?\s*$/
    )

    if (isTelephone) return true

    return `The value entered in the ${content.field} field is not a valid phone number.`
  })

  defineRule('chk_for_font_format_text', value => {
    if (
      Misc.validateCustomRegex(
        value.toLowerCase(),
        /.*\b(?:otf|ttf|woff)\b.*/
      )
    ) {
      return get(Content, 'pages.submissions.components.descriptions.description.font_format_error')
    }
    return true
  })

  defineRule('family_note_rules', (value, _, context) => {
    const strippedString = value.replace(/(<([^>]+)>)/gi, '');
    if (strippedString?.length > 64000) {
      return t('pages.inventory_ingestion_hub.family_note_rules.too_long')
    } 
    return true
  })

  defineRule('min_bundle_fonts', (value, params) => {
    if (!isArray(value)) return false
    if (isEmpty(value)) return false

    return value.length >= Number(params[0])
  })

  defineRule('bundle_name_required', (value, params) => {
    return value.trim().length > 0
  })

  defineRule('submission_tag_required', (value) => {
    let tags = SubmissionComputed.submissionTags.get();
    if (isEmpty(tags)) {
      return `Please assign some tags to your typeface for better discovery.`;
    }
    return true;
  });

  defineRule('submission_designer_required', (value) => {
    if (isEmpty(value)) {
      return `The font family designers field is required.`;
    }
    return true;
  });

  defineRule('submission_description_required', (value) => {
    if (isEmpty(value)) {
      return 'Please fill in the mandatory font family description.';
    }
    return true;
  });

  defineRule('foundry_location_required', (value, _, context) => {
    if (isEmpty(value)) {
      if (context.field === "headOfficeState") {
        return `Please select state`
      }
      if (context.field === "headOfficeCountry") {
        return `Please select country`
      }
    }
    return true;
  });

  defineRule('bundle_description_required', (value, params) => {
    return value.trim().length > 0
  })

  defineRule('atleast_three_image', (value, params) => {
    if (!isArray(value)) return false
    if (isEmpty(value)) return false
    return true
  })


  defineRule('editor_description', (value, _, context) => {
    const invalid = Misc.validateCustomRegex(value, /^\s*<\s*p\s*>\s*<\/\s*p\s*>\s*$/)
    if (invalid) {
      return `The ${context.name} field is required.`
    }

    return true
  })

  defineRule('foundryinfo_required', (value, _, context) => {
    let field = ''
    if (context.rule.params[0] === 'primary_contact') {
      field = 'primary contact'
    }

    if (context.rule.params[0] === 'email') {
      field = 'email address'
    }
    if (context.rule.params[0] === 'address1' && isEmpty(value)) {
      return `Enter the postal address of your primary contact.`
    }

    if (context.rule.params[0] === 'city' && isEmpty(value)) {
      return `Select the city where your primary contact is located.`
    }

    if (context.rule.params[0] === 'zipcode' && isEmpty(value)) {
      return `Enter the zip code of the primary contact's city. `
    }
    if (isEmpty(value)) {
      return `Enter the name of your ${field}.`
    }
    return true
  })

  defineRule('existing_mid_error', (value, _, context) => {
    return t('pages.rules_Error.existing_mid_error');
  })

  defineRule('family_name_rules', (value, _, context) => {
    if (isEmpty(value)) {
      return t('pages.inventory_ingestion_hub.family_name_rules.required_display_name');
    }
    if (value.length < MIN_SIZE_INGESTION_FAMILYNAME) {
      const errorMessage = t('pages.inventory_ingestion_hub.family_name_rules.min_limit_family_name', { limit: MIN_SIZE_INGESTION_FAMILYNAME })
      return errorMessage
    }
    if (value.length > MAX_SIZE_INGESTION_FAMILYNAME) {
      const errorMessage = t('pages.inventory_ingestion_hub.family_name_rules.max_limit_family_name', { limit: MAX_SIZE_INGESTION_FAMILYNAME })
      return errorMessage
    }

    if (!ALLOWED_CHARS_PATTERN_FOR_FAMILY_NAME.test(value)) {
      return t('pages.inventory_ingestion_hub.family_name_rules.allowed_chars');
    }
    return true;
  });

  defineRule('new_custom_family_rules', (value, _, context) => {
    if (isEmpty(value)) {
      return t('pages.inventory_ingestion_hub.new_custom_family_rules.family_name_required');
    }
    if (value.length < MIN_SIZE_INGESTION_FAMILYNAME) {
      const errorMessage = t('pages.inventory_ingestion_hub.family_name_rules.min_limit_family_name', { limit: MIN_SIZE_INGESTION_FAMILYNAME })
      return errorMessage
    }
    if (value.length > MAX_SIZE_INGESTION_FAMILYNAME_CUSTOM) {
      return t('pages.inventory_ingestion_hub.new_custom_family_rules.family_name_max_length');
    }
    if(value.trim().toLowerCase() === context.rule.params[0].trim().toLowerCase()) {
      return t('pages.inventory_ingestion_hub.new_custom_family_rules.family_name_exists');
    }
    if (!ALLOWED_CHARS_PATTERN_FOR_FAMILY_NAME.test(value)) {
      return t('pages.inventory_ingestion_hub.family_name_rules.allowed_chars');
    }
    return true;
  });

  defineRule('minimum_familyPrice', (value, _, contxt) => {
    if (value == '') {
      return t('pages.inventory_ingestion_hub.family_price_rules.family_price_required');
    }
    if (!isEmpty(value) && value <= 4) {
      return t('pages.inventory_ingestion_hub.family_price_rules.min_pricing');
    }

    if (!isEmpty(value) && value > 10000) {
      return t('pages.inventory_ingestion_hub.family_price_rules.max_pricing');
    }
    return true
  })

  defineRule('date_selected', (value, _, context) => {
    if (value == '') {
      return t('pages.inventory_ingestion_hub.date_selected_rules.release_date_required');
    }
    return true
  })

  defineRule('only_numeric_mid', (value, _, context) => {
    if (value.length > 0 && !/^\d+$/.test(value)) {
      return t('pages.rules_Error.only_numeric_mid')
    }

    if (value.length > 0 && value.length < MIN_ORDER_MID_LENGTH) {
      return t('pages.inventory_ingestion_hub.dropzone.order_mid_error_message')
    }
    return true
  });

  defineRule('validate_project_name', (value, _, context) => {
    if (isEmpty(value)) {
      return t('pages.inventory_ingestion_hub.sis_project_name_rules.project_name_required');
    }
    if (value.length < 3) {
      return t('pages.inventory_ingestion_hub.sis_project_name_rules.min_limit');
    }
    // Disallow emojis (Unicode range check)
    if (EMOJI_REGEX.test(value)) {
      return t('pages.inventory_ingestion_hub.sis_project_name_rules.disallow_emoji');
    }
    // Specifically check for <script> tag even in broken forms
    if (SCRIPT_TAG_REGEX.test(value)) {
      return t('pages.inventory_ingestion_hub.sis_project_name_rules.no_script_tags');
    }
    // Disallow any HTML tags
    if (
      SAFE_HTML_TAG_NAME_REGEX.test(value) || // Matches common HTML tags to block their usage in project names.
      SAFE_TAG_ATTR_REGEX.test(value) || // Matches common HTML tag attributes to block their usage in project names.
      SAFE_ENCODED_HTML_TAG_REGEX.test(value) || // Detects encoded HTML tags to prevent bypassing tag validation.
      HTML_SYMBOLS_REGEX.test(value) || // Matches common HTML symbols to block their usage in project names.
      SAFE_ENCODED_TAG_ATTR_REGEX.test(value) || // Identifies encoded HTML tag attributes to ensure input safety.
      ENCODED_ANGLE_BRACKETS_REGEX.test(value) // Prevents encoded angle brackets from being used to form HTML tags.
    ) {
      return t('pages.inventory_ingestion_hub.sis_project_name_rules.no_html_tags');
    }
    return true;
  });

  defineRule('style_name_validation', (value, _, context) => {
    if (isEmpty(value)) {
      return true;
    }
    if (!/^[\p{Letter}\p{Number}\- ]+$/u.test(value)) {
      return t('pages.inventory_ingestion_hub.style_name_validation.accepted_char');
    }
    return true;
  });

  defineRule('replace_style_name_validation', (value, _, context) => {
    if (isEmpty(value)) {
      return true;
    }
    if (!validateAlphanumeric(value)) {
      return t('pages.inventory_ingestion_hub.family_bulk_edit.bulk_refine_styles_modal.invalid_style_name_error');
    }
    return true;  
  });

  defineRule('inventory_contractNumber_rules', (value, param) => {
    const regex = /^[a-zA-Z0-9,\- ]*$/;
    let inputNumbers = value.split(',').map(num => num.trim());
    inputNumbers = inputNumbers.filter(num => num !== '');
    const duplicatesExist = inputNumbers.some((num, index) =>
      inputNumbers.indexOf(num) !== index
    );
    if (!value || value.trim() === '') {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_required');
    }
    if (!regex.test(value)) {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_specialCharacter');
    }
    if (duplicatesExist) {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_duplicate');
    }
    return true;
  });

  defineRule('new_family_rules', (value, [existingFamilyNames, apiErrorMessage]) => {
    if (isEmpty(value)) {
      return t('pages.inventory_ingestion_hub.split_family_modal.family_name_required');
    }

    // Only allow letters, numbers, hyphens, and spaces
    if (!/^[\p{Letter}\p{Number}\- ]+$/u.test(value)) {
      adobeCjaEventEmitter({
        event: CJA_EVENTS.LINK_CLICK,
        links: {
          linkCategory: LINK_CATEGORIES.INVENTORY_INGESTION_HUB,
          linkSection: LINK_SECTION.CREATE_NEW_FAMILY_FROM_STYLES_OVERLAY,
          linkName: LINK_NAMES.ERROR_FAMILY_NAME_INVALID_CHARACTERS,
          linkType: LINK_TYPES.SYSTEM
        }
      });
      return t('pages.inventory_ingestion_hub.split_family_modal.accepted_char');
    }
    
    // existingFamilyNames is already an array when using object syntax
    const familyNamesList = Array.isArray(existingFamilyNames) ? existingFamilyNames : [];
    
    // Check if family name already exists (case insensitive)
    const familyAlreadyExists = familyNamesList.some(familyName => 
      familyName && familyName.toLowerCase().trim() === value.toLowerCase().trim()
    );
    
    if (familyAlreadyExists) {
      adobeCjaEventEmitter({
        event: CJA_EVENTS.LINK_CLICK,
        links: {
          linkCategory: LINK_CATEGORIES.INVENTORY_INGESTION_HUB,
          linkSection: LINK_SECTION.CREATE_NEW_FAMILY_FROM_STYLES_OVERLAY,
          linkName: LINK_NAMES.ERROR_FAMILY_NAME_ALREADY_USED,
          linkType: LINK_TYPES.SYSTEM
        }
      });
      return t('pages.inventory_ingestion_hub.split_family_modal.family_name_exists');
    }
    
    // If there's an API error message, show it
    if (apiErrorMessage && apiErrorMessage.trim()) {
      return apiErrorMessage;
    }
    
    return true;
  });

  defineRule('dropship_contractNumber_rules', (value, param) => {
    const regex = /^[a-zA-Z0-9,\- ]*$/;
    let inputNumbers = value.split(',').map(num => num.trim());
    inputNumbers = inputNumbers.filter(num => num !== '');
    const duplicatesExist = inputNumbers.some((num, index) =>
      inputNumbers.indexOf(num) !== index
    );
    if (!value || value.trim() === '') {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_required');
    }
    if (!regex.test(value)) {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_specialCharacter');
    }
    if (duplicatesExist) {
      return t('pages.inventory_ingestion_hub.fontFile_info_table.contract_number_duplicate');
    }

    if (value.includes(',')) {
      return t('pages.dropShip.flip_fonts_listing.contract_number_single_value');
    }

    return true;
  });


  defineRule('inventory_price_rules', (value) => {
    if (value <= 0) {
      return t('pages.inventory_ingestion_hub.inventory_price_rules.min_price');
    }

    if (value > 10000) {
      return t('pages.inventory_ingestion_hub.inventory_price_rules.max_limit');
    }
    return true;
  })

  defineRule('postal_code_rule', (value, [countryCode], context) => {
  if (!value || !countryCode) return true; // Let required rule handle empty
  
  // Normalize input
  const normalizedValue = value.trim().toUpperCase();
  const alphaNumericSpaceHyphenRegex = /^[a-zA-Z0-9 -]+$/;
  // Check if the postal code contains only alphanumeric, spaces, and hyphens
  if (!alphaNumericSpaceHyphenRegex.test(normalizedValue)) {
    const fieldName = context.field
    return `The ${fieldName} field should have alpha numeric characters, spaces and hyphens only.`
  }

  return true;
});
  
  configure({
    generateMessage: context => {
      const fieldName = context.field.toLowerCase()
      const messages = {
        required: `The ${fieldName} field is required.`,
        // fou_numbers: `The ${fieldName} field should be a valid price.`,
        is: `The ${fieldName} must be ${context.rule.params[0]}.`,
        fou_slides: `For the image carousel, we recommend adding between ${context.rule.params[0]} and ${context.rule.params[1]} images.`,
        min: `The ${fieldName} field must be at least ${context.rule.params[0]} characters.`,
        max: `The ${fieldName} field may not be greater than ${context.rule.params[0]} characters.`,
        between: `The ${fieldName} must be between ${context.rule.params[0]} and  ${context.rule.params[1]}.`,
        numeric: `The ${fieldName} field may only contain numeric characters.`,
        alpha_num: `The ${fieldName} field may only contain alpha-numeric characters. `,
        alpha_spaces: `The ${fieldName} field can only contain letters and spaces.`,
        email: `The ${fieldName} field is not a valid email.`,
        min_value: `The ${fieldName} field is too low.`,
        max_value: `The ${fieldName} field is too high.`,
        excluded: 'This field is not allowed.',
        country_excluded: 'We do not allow users from this location',
        password_mismatch: `The ${fieldName} field does not match.`,
        numeric_only_no_space: `The ${fieldName} field should only contain numeric characters with no extra space.`,
        decimal: `The ${fieldName} field must be a proper decimal and may contain ${context.rule.params[0]} decimal points.`,
        validate_max_date: `The ${fieldName} should be less than or same as current date`,
        validate_min_date: `The ${fieldName} should be greater than Jan 01, 2010`,
        validate_end_date: 'The end date should be greater than or same as start date.',
        custom_regex: `The ${fieldName} format is not in valid format.`,
        custom_numeric: `The ${fieldName} field should only contain formatted numeric characters`,
        alpha_numeric_no_space: `The ${fieldName} field should only contain alphanumeric characters with no extra space.`,
        numeric_only: `The ${fieldName} field should only contain numeric characters.`,
        alpha_numeric: `The ${fieldName} field should only contain alphanumeric characters.`,
        promo_back_date: `The start date should be from ${Misc.formatDate(context.rule.params[0], 'll')}.`,
        checkUniqueSecretCode: 'Sorry, someone else is already using that secret code! Please try again.',
        max_days: `The promotion period cannot exceed ${context.rule.params[2]} days.`,
        check_1_day: 'The promotion period should be atleast 1 day.',
        bundle_image: `The ${fieldName} must be between ${context.rule.params[0]} and  ${context.rule.params[1]}.`,
        inspirational_image: `The ${fieldName} must be between ${context.rule.params[0]} and  ${context.rule.params[1]}.`,
        edit_max_days: `The promotion period cannot exceed ${context.rule.params[0]} days.`,
        font_types_string: `Please do not include the file type in your ${fieldName.charAt(0).toLowerCase() + fieldName.slice(1)
          }.`,
        lengthChar: `The ${fieldName} field may not be greater than ${Number(context.rule.params[0])} characters.`,
        required_keys: `The ${fieldName} field is required.`,
        custom_max_char: 'Character limit exceeded',
        required_or_keys: `The ${fieldName} field is required.`,
        required_key_values: `The ${fieldName} field is required.`,
        min_bundle_fonts: `You must select at least two fonts to create a bundle.`,
        editor_description: `The ${fieldName} is not supported.`,
        bundle_name_required: `Give your bundle a name.`,
        image_ratio: `Check the ratio of your bundle image. It should be 2:1 and minimum resolution should be 1440 x 720 px.`,
        image_size: `The size of your image exceeds our requirements. Please resubmit with a smaller file size.`,
        atleast_one_foundry: `You need to select at least one foundry.`,
        bundle_description_required: `You have to enter a bundle description.`,
        custom_min_char_no_html_bundles: `You have to enter a bundle description.`,
        bundle_syncing: `We are syncing your bundle <bundle name>. Remember that you can only edit your bundle after four hours of syncing.`,
        invalid_amount: `You entered an invalid price. Please review.`,
        atleast_three_image: `Upload at least three images associated with your bundle.`,
        max_date_2_years: `You can only schedule promotions up to the next two years.`,
        no_emoji: `The promotion title cannot include emojis.`,
        max_discount_value: `Discount is too high!`
      }
      const message = messages[context.rule.name] ? messages[context.rule.name] : `The ${fieldName} field is invalid`
      return message
    },
    validateOnBlur: true,
    validateOnChange: true,
    validateOnInput: true,
    validateOnModelUpdate: true
  })
}

export { configureVeeValidateComponents, configureVeeValidateRules }
